/// <amd-module name="Feed/Legacy/Stores/GlobalStore" />
import * as React from "react";
import { IModuleStore } from "redux-dynamic-modules";

import { ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { IReduxStoreService } from "VSS/Redux/StoreService";
import * as Store from "VSS/Reflux/Store";

import { Link } from "VSSUI/Link";
import { TextField } from "VSSUI/TextField";

import { HubActionStrings } from "Feed/Common/Constants/Constants";
import { Package } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { IError } from "Feed/Common/Types/IError";
import { IGeneralDialogProps } from "Feed/Common/Types/IGeneralDialogProps";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub/FeedHubActions";
import { NavigationActions } from "Feed/Core/Redux/Navigation/NavigationActions";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { RecycleBinActions } from "Feed/Legacy/Actions/RecycleBinActions";
import { IPackageDependencySelectedPayload } from "Feed/Legacy/Common/ActionPayloads";

import { NavigationHandler } from "../Common/NavigationHandler";
import { Feed } from "../WebApi/VSS.CustomFeed.Contracts";

// tslint:disable:interface-name
export interface GlobalState {
    dialogProps: IGeneralDialogProps;
}

export class GlobalStore extends Store.Store {
    private pageContext: IVssPageContext;

    private reduxStore: IModuleStore<any>;

    constructor(pageContext: IVssPageContext) {
        super("feed-GlobalStore");
        this.pageContext = pageContext;
        this._currentState = {} as GlobalState;
        this._currentState.dialogProps = null;
        this._initializeActionListeners();

        const storeService = pageContext.getService<IReduxStoreService>("redux-store-service");
        this.reduxStore = storeService.getStore(null);
    }

    public renderHub = (data: { action: string; error?: IError }): void => {
        this.reduxStore.dispatch(FeedHubActions.setErrorMessage(data.error));
        this.reduxStore.dispatch(NavigationActions.navigateToHub(data.action));
    };

    public getGlobalState(): GlobalState {
        return this._currentState;
    }

    public setActionAndTriggerViewRender(action: string): void {
        this.renderHub({ action: action, error: undefined });
    }

    public setErrorAndTriggerViewRender(action: string, error: IError): void {
        this.renderHub({ action: action, error: error });
    }

    private _initializeActionListeners(): void {
        Actions.NavigateToHub.addListener(this.renderHub);
        Actions.ErrorDismissed.addListener(this._handleErrorDismissed);
        Actions.ErrorEncountered.addListener(this._handleErrorEncountered);
        Actions.PackageSelected.addListener(this._handlePackageSelectedAction);
        Actions.PackageDependencySelected.addListener(this._handlePackageDependencySelectedAction);
        Actions.CreateFeedNavigateClicked.addListener(this._handleCreateFeedClickedAction);
        Actions.CreateFeedCanceled.addListener(this._handleCreateFeedCanceledAction);
        Actions.DialogOpenChanged.addListener(this._handleDialogOpenChangedAction);

        RecycleBinActions.PackageSelected.addListener(this._handleRecyleBinPackageSelectedAction);

        // Closing the dialog after the action has been completed
        Actions.PackageDeletedCompleted.addListener(this._closeGeneralDialog);
        Actions.PackageVersionDeprecatedCompleted.addListener(this._closeGeneralDialog);
        Actions.PackageListedChangedCompleted.addListener(this._closeGeneralDialog);
        RecycleBinActions.PackagesPermanentDeletedCompleted.addListener(this._closeGeneralDialog);
    }

    // all listeners must be removed, otherwise actions may be invoked multiple times
    public dispose(): void {
        Actions.NavigateToHub.removeListener(this.renderHub);
        Actions.ErrorDismissed.removeListener(this._handleErrorDismissed);
        Actions.ErrorEncountered.removeListener(this._handleErrorEncountered);
        Actions.PackageSelected.removeListener(this._handlePackageSelectedAction);
        Actions.PackageDependencySelected.removeListener(this._handlePackageDependencySelectedAction);
        Actions.CreateFeedNavigateClicked.removeListener(this._handleCreateFeedClickedAction);
        Actions.CreateFeedCanceled.removeListener(this._handleCreateFeedCanceledAction);
        Actions.DialogOpenChanged.removeListener(this._handleDialogOpenChangedAction);

        RecycleBinActions.PackageSelected.removeListener(this._handleRecyleBinPackageSelectedAction);

        Actions.PackageDeletedCompleted.removeListener(this._closeGeneralDialog);
        Actions.PackageVersionDeprecatedCompleted.removeListener(this._closeGeneralDialog);
        Actions.PackageListedChangedCompleted.removeListener(this._closeGeneralDialog);
        RecycleBinActions.PackagesPermanentDeletedCompleted.removeListener(this._closeGeneralDialog);
    }

    private _handleErrorEncountered = (error: IError): void => {
        this.reduxStore.dispatch(FeedHubActions.setErrorMessage(error));
    };

    private _handleErrorDismissed = (): void => {
        this.reduxStore.dispatch(FeedHubActions.clearErrorMessage());
    };

    private _closeGeneralDialog = (): void => {
        this.reduxStore.dispatch(FeedHubActions.dismissDialog());
    };

    private _handlePackageDependencySelectedAction = (dependency: IPackageDependencySelectedPayload): void => {
        this.reduxStore.dispatch(
            FeedHubActions.packageDependencySelected(dependency.dependency, dependency.originPackage)
        );
    };

    private _handlePackageSelectedAction = (packageSummary: Package): void => {
        this.reduxStore.dispatch(FeedHubActions.packageSelected(packageSummary));
    };

    private _handleRecyleBinPackageSelectedAction = (packageSummary: Package): void => {
        this.reduxStore.dispatch(FeedHubActions.recycleBinPackageSelected(packageSummary));
    };

    private _handleCreateFeedClickedAction = (): void => {
        this.renderHub({ action: HubActionStrings.CreateFeed, error: undefined });
    };

    private _handleCreateFeedCanceledAction = (feed: Feed): void => {
        NavigationHandler.renderFeed(this.pageContext, feed);
    };

    private _handleDialogOpenChangedAction = (dialogProps: IGeneralDialogProps): void => {
        if (dialogProps === null) {
            this.reduxStore.dispatch(FeedHubActions.dismissDialog());
        } else {
            if (dialogProps.textField) {
                var textFieldValue = new ObservableValue<string>(dialogProps.textFieldDefaultValue);
            }
            this.reduxStore.dispatch(
                FeedHubActions.createDialog({
                    headerText: dialogProps.headerText,
                    dialogBody: dialogProps.confirmTextLearnMoreLink ? (
                        <div>
                            {dialogProps.confirmText}
                            <Link
                                href={dialogProps.confirmTextLearnMoreLink}
                                target="_blank"
                                className="dialog-link padding-left-4"
                            >
                                {PackageResources.LearnMore}
                            </Link>
                            )
                            {dialogProps.textField && (
                                <TextField
                                    inputClassName="dialog-content-textarea"
                                    value={textFieldValue}
                                    label={dialogProps.textFieldLabel}
                                    placeholder={dialogProps.textFieldPlaceholder}
                                    rows={3}
                                    multiline={true}
                                    resizable={false}
                                    maxLength={255}
                                    onChange={(_, value) => {
                                        textFieldValue.value = value;
                                    }}
                                />
                            )}
                        </div>
                    ) : (
                        dialogProps.confirmText
                    ),
                    className: dialogProps.dialogClassName,
                    warningBody: (dialogProps.severeWarningText || dialogProps.severeWarningLearnMoreLink) && (
                        <div>
                            {dialogProps.severeWarningText && dialogProps.severeWarningText}
                            {dialogProps.severeWarningLearnMoreLink && (
                                <Link
                                    className="dialog-link padding-left-4"
                                    href={dialogProps.severeWarningLearnMoreLink}
                                    target="_blank"
                                >
                                    {PackageResources.LearnMore}
                                </Link>
                            )}
                        </div>
                    ),
                    primaryButtonText: dialogProps.saveButtonText,
                    primaryButtonLoadingText: dialogProps.onSavePrimaryButtonText,
                    primaryButtonClassName: dialogProps.saveButtonClassName,
                    primaryButtonDanger: dialogProps.saveButtonClassName === "delete-button",
                    primaryButtonCallback: dialogProps.textField
                        ? () => {
                              dialogProps.onSaveCallback(textFieldValue.value);
                          }
                        : dialogProps.onSaveCallback,
                    onDismissCallback: dialogProps.onDismissCallback
                })
            );
        }
    };

    private _currentState: GlobalState;
}
